import type { CurrentUser } from './api/currentUser';

/**
 * The platform operator. Not a tenant role.
 *
 * <p>The value is a `role_key` as `02_role_seed.sql` spells it, and the API sends the
 * keys rather than display names precisely so a rename on screen cannot break a check
 * here — the same reasoning that keeps `product_key` out of the catalogue rename.
 */
export const SUPER_ADMIN = 'super-admin';

/**
 * Whether this account administers the platform itself.
 *
 * <h2>Why this is a function and not a boolean on the session</h2>
 *
 * `CurrentUser.roles` is what the server sent, and it stays that. A derived flag stored
 * beside it is a second copy of one fact, and the copy is the one that goes stale — a
 * role revoked between two renders would leave the flag saying yes.
 *
 * <h2>What it is not for</h2>
 *
 * **Not security.** Every `@SuperAdminOnly` endpoint refuses a caller on its own, and
 * that refusal is the rule; this decides what somebody is *shown*. A menu is never a
 * gate — the backend's `CLAUDE.md` says so of the left-hand menu, and it is true of this
 * too, because typing a URL bypasses anything decided here.
 *
 * <p>So the worst a bug in this function can do is show an operator screen to somebody
 * who then gets 403s from it, or hide one from somebody who can still reach it by URL.
 * Both are wrong and neither is a breach.
 */
export function isSuperAdmin(user: CurrentUser | null | undefined): boolean {
  return user?.roles.includes(SUPER_ADMIN) ?? false;
}
